import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { all, call, debounce, delay, fork, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { updateOrgAgencies } from '../actions/agencies';
import { updateAppsData } from '../actions/apps';
import { fetchedFields } from '../actions/fields';
import { UPDATE_USER_DATA } from '../actions/user';
import client from '../apollo';
import { updateAllFields, updateFilterFields } from '../sagas/fields';
import { getAppsList } from '../selectors/apps';
import { getFieldsInitiated } from '../selectors/fields';
import { getTranslate } from '../selectors/locale';
import { getUserData } from '../selectors/user';
import AgenciesService from '../services/agencies';
import AppsAPI from '../services/apps';
import DataSourcesAPI from '../services/dataSources';
import FeaturesAPI from '../services/features';
import SectionsAPI from '../services/sections';
import {
    addFutureChanged,
    addMessageToGroup,
    AGENCY_SAVED,
    CLOSE_AGENCY_DIALOG_CALLED,
    closeAgencyDialog,
    CONFIRM_CHANGES_ACCEPTED,
    CONFIRM_CHANGES_REJECTED,
    CREATE_NEW_MEMBER,
    DELETE_MEMBER_CLICKED,
    DELETE_USER_DISABLED,
    DELETE_USER_INVITE_REVOKED,
    fetchSectionFailed,
    fetchSectionRequested,
    fetchSectionSucceeded,
    getOrgInvites,
    getOrgUsers,
    HIDE_PAGE_CALLED,
    hidePage,
    INVITE_CREATED,
    MANAGE_PERMISSIONS_CALLED,
    MANAGE_PERMISSIONS_FINISHED,
    managePermissions,
    modifyInvitationState,
    RESEND_INVITATION,
    resetSectionPagination,
    saveMemberError,
    saveMemberSuccess,
    SECTION_SEARCH_INPUT_CHANGED,
    SECTION_SORT_CLICKED,
    setDimensionFilter,
    setInitialPermissionSnapshot,
    setAgenciesLegacyFetching,
    setSectionOverallTotal,
    setSectionSearch,
    setSectionSort,
    showConfirmChangesDialog,
    teamManagementLoaded,
    UPDATE_AGENCY_SELECTION_FORM,
    UPDATE_DATA_SOURCE_DATA,
    updateAppsList,
    updateDataSourceData,
    updateFeaturesDict,
    updatePartnersData,
    updateRoleSelectionForm,
    updateOrgSdkEvents,
    updateSectionsDict,
    USER_PERMISSIONS_UPDATED,
    userDisabled,
    userInviteRevoked,
} from './actions';
import { SECTION_KEYS } from './reducer';
import { GET_BASE_SOURCE_PARTNERS, GET_ALL_EVENTS } from './queries';
import {
    getActiveMemberType,
    getBaseSourcesLoading,
    getEditMemberId,
    getPermissionState,
    getSaveMemberData,
    makeGetEditUserState,
} from './selectors';
import TeamManagementAPI from './service';
import { getRoleDisplayName, getUserDisplayName, MEMBER_GROUP_TYPES, MEMBER_TYPES } from './utils';
import { reportTypes } from '../utils/reports';

const appsApi = new AppsAPI();
const dataSourcesApi = new DataSourcesAPI();
const sagas = new TeamManagementAPI();
const featuresApi = new FeaturesAPI();
const sectionsApi = new SectionsAPI();
const agenciesApi = new AgenciesService();

function* fetchOrgAgencies() {
    yield put(setAgenciesLegacyFetching(true));
    try {
        const agencies = yield call(agenciesApi.getOrgAgencies);
        yield put(updateOrgAgencies(agencies));
    } catch (e) {
        console.error(e);
        yield put(updateOrgAgencies([])); // or handle error appropriately
    } finally {
        yield put(setAgenciesLegacyFetching(false));
    }
}

// /api/org_networks is a 365-day Druid groupBy that can take a minute under Druid load, and
// getBaseSources only reads it for non-unified orgs (unified orgs use the GraphQL partners).
export function* fetchUansIfNeeded() {
    let userData = yield select(getUserData);
    if (Object.keys(userData).length === 0) {
        yield take(UPDATE_USER_DATA);
        userData = yield select(getUserData);
    }
    if (userData.is_unified) {
        return {};
    }
    return yield call(dataSourcesApi.getBaseSources, {});
}

// On failure uans stays null (loading) on purpose: an empty list would let a "not in" source restriction
// save as "all sources", so Save stays blocked rather than broadening access.
function* fetchUansInBackground() {
    try {
        const uans = yield call(fetchUansIfNeeded);
        yield put(updateDataSourceData(uans));
    } catch (error) {
        console.error('Failed to fetch org networks:', error);
    }
}

function* updatePageData() {
    yield fork(fetchUansInBackground);

    const initiated = yield select(getFieldsInitiated);

    if (!initiated) {
        yield call(updateAllFields);
    }

    yield call(updateFilterFields, reportTypes.mainReport);

    // Note: /api/agencies/self is already fetched by the global bootstrap in utils/init.js
    // (which populates state.orgAgencies for app-wide consumers). Calling fetchOrgAgencies
    // here too would duplicate that slow request — see PR perf review. The team-mgmt page's
    // visible rows come from sections.agencies (paginated), so this saga doesn't need it.
    const [usersResp, invitesResp, appsResp, partnersResp, featuresDictResp, sectionsDictResp] = yield all([
        call(sagas.getOrgUsers),
        call(sagas.getOrgInvites),
        call(appsApi.getApps, true),
        call(client.query, { query: GET_BASE_SOURCE_PARTNERS }),
        call(featuresApi.getFeaturesDict),
        call(sectionsApi.getSectionsDict),
    ]);

    yield put(getOrgUsers(usersResp));
    yield put(getOrgInvites(invitesResp));
    yield put(updateAppsData(appsResp));
    yield put(updatePartnersData(partnersResp?.data?.baseSourcePartners || []));
    yield put(updateFeaturesDict(featuresDictResp));
    yield put(updateSectionsDict(sectionsDictResp));

    // Mark page as loaded BEFORE fetching events so page is interactive immediately
    yield put(fetchedFields());
    yield put(teamManagementLoaded());

    // Fetch SDK events in background (non-blocking)
    yield fork(fetchSdkEventsInBackground);

    const appsListResp = yield select(getAppsList);
    yield put(updateAppsList(appsListResp));
}

function* fetchSdkEventsInBackground() {
    try {
        const eventsResp = yield call(client.query, { query: GET_ALL_EVENTS });
        yield put(updateOrgSdkEvents(eventsResp?.data?.events || []));
    } catch (error) {
        console.error('Failed to fetch SDK events:', error);
        // Set empty array on error so UI shows "no events" instead of loading forever
        yield put(updateOrgSdkEvents([]));
    }
}

function* updateOrgUsers(disableBrowserCache) {
    const response = yield call(sagas.getOrgUsers, disableBrowserCache);
    yield put(getOrgUsers(response));
}

function* updateOrgInvites(disableBrowserCache) {
    const response = yield call(sagas.getOrgInvites, disableBrowserCache);
    yield put(getOrgInvites(response));
}

function* handleDeleteUser(user) {
    try {
        let group = '';

        if (user.status === 'Member') {
            const { result } = yield call(sagas.disableUser, user.organization_user_id);
            if (result) {
                yield put(userDisabled(user));
                group = 'active';
            }
        } else {
            const { result } = yield call(sagas.revokeInvitation, user.invite_id);
            if (result) {
                yield put(userInviteRevoked(user));
                group = 'pending';
            }
        }

        if (group) {
            const translate = yield select(getTranslate);
            yield put(
                addMessageToGroup(
                    group,
                    translate('STATIC.PAGES.TEAM_MEMBERS.USER_DELETED', { userName: getUserDisplayName(user) }),
                    'success'
                )
            );
        }
    } catch (e) {
        yield Promise.resolve();
    }
}

function* handleDeleteAgency(agency) {
    try {
        yield call(sagas.deleteOrgAgency, agency.id);
        const translate = yield select(getTranslate);
        yield put(
            addMessageToGroup(
                MEMBER_GROUP_TYPES.active,
                translate('STATIC.PAGES.TEAM_MEMBERS.AGENCY_DELETED', { agencyName: agency.name }),
                'success'
            )
        );
        // Refresh the visible table (paginated section) immediately so the deleted row
        // disappears. The legacy global state.orgAgencies (read by AgencyDialog, the "used
        // in" feature, partner config, link mgmt, and a few selectors) is refreshed in the
        // background — it's slow and must not block the saga. Consumers may briefly see
        // stale data; the Add Agency button is disabled during the refetch (see page.jsx).
        yield call(refreshSectionAfterDelete, 'agencies');
        yield fork(fetchOrgAgencies);
    } catch (e) {
        yield put(saveMemberError(e.error));
    }
}

function* handleDeleteMemberClicked({ member }) {
    const activeMemberType = yield select(getActiveMemberType);

    if (activeMemberType === MEMBER_TYPES.AGENCY) {
        yield call(handleDeleteAgency, member);
    } else {
        yield call(handleDeleteUser, member);
    }
}

function* handleResendInvitation(action) {
    const { user } = action;
    try {
        yield put(modifyInvitationState(user, { inProgress: true, disabled: true }));
        const [{ result }] = yield all([call(sagas.resendInvitation, user.invite_id), delay(3000)]);
        if (result) {
            const translate = yield select(getTranslate);
            yield put(
                addMessageToGroup(
                    MEMBER_GROUP_TYPES.pending,
                    translate('STATIC.PAGES.TEAM_MEMBERS.RE_INVITE_SENT', { userName: getUserDisplayName(user) }),
                    'success'
                )
            );
            yield put(
                modifyInvitationState(user, {
                    inProgress: false,
                    disabled: true,
                    last_re_invited_at: moment.utc() / 1000,
                })
            );
        }
    } catch (e) {
        yield Promise.resolve();
    }
}

function* handleCreateNewUser(role, registrationForm, recaptchaToken) {
    const { type: roleType } = role;
    try {
        const { result } = yield call(sagas.sendInvitation, { ...registrationForm, role, recaptchaToken });
        if (result) {
            const translate = yield select(getTranslate);
            const userName = getUserDisplayName(registrationForm, {
                first_name: 'firstName',
                last_name: 'lastName',
            });
            yield put(saveMemberSuccess(userName));
            yield put({ type: INVITE_CREATED });
            yield put(
                addMessageToGroup(
                    MEMBER_GROUP_TYPES.pending,
                    translate('STATIC.PAGES.TEAM_MEMBERS.INVITE_INFO', {
                        userName,
                        role: getRoleDisplayName(roleType),
                    }),
                    'success'
                )
            );
            yield call(updateOrgInvites, true);
        }
    } catch (e) {
        yield put(saveMemberError(e.error));
    }
}

function* handleManageUserPermissions(editUserId, role, registrationForm) {
    try {
        const { result } = yield call(sagas.updateOrgUser, editUserId, role);
        if (result) {
            yield call(updateOrgUsers);
            const translate = yield select(getTranslate);
            const userName = getUserDisplayName(registrationForm, { first_name: 'name', last_name: 'lastname' });
            yield put(saveMemberSuccess(userName));
            yield put({ type: USER_PERMISSIONS_UPDATED });
            yield put(
                addMessageToGroup(
                    'active',
                    translate('STATIC.PAGES.TEAM_MEMBERS.USER_SAVED_MESSAGE', {
                        userName,
                    }),
                    'success'
                )
            );
        }
    } catch (e) {
        yield put(saveMemberError(e.error));
    }
}

function* handleManageAgencyPermissions({ id, name }, role) {
    try {
        yield call(sagas.updateOrgAgency, id, role);
        // Refresh the legacy global state.orgAgencies in the background — it's only used
        // by the "used in" feature and is slow; saving permissions shouldn't block on it.
        // The new agencies section slice is refreshed via the AGENCY_SAVED trigger saga.
        yield fork(fetchOrgAgencies);
        const translate = yield select(getTranslate);
        yield put(saveMemberSuccess(name));
        yield put({ type: AGENCY_SAVED });
        yield put(
            addMessageToGroup(
                MEMBER_GROUP_TYPES.active,
                translate('STATIC.PAGES.TEAM_MEMBERS.AGENCY_SAVED_MESSAGE', { agencyName: name }),
                'success'
            )
        );
    } catch (e) {
        yield put(saveMemberError(e.error));
    }
}
function* handleManagePermissionsFinished(action) {
    const activeMemberType = yield select(getActiveMemberType);
    const { editMemberId, registrationForm, role } = yield select(getSaveMemberData);

    if (activeMemberType === MEMBER_TYPES.AGENCY) {
        yield call(handleManageAgencyPermissions, registrationForm, role);
        return;
    }

    if (editMemberId) {
        yield call(handleManageUserPermissions, editMemberId, role, registrationForm);
    } else {
        const { recaptchaToken } = action;
        yield call(handleCreateNewUser, role, registrationForm, recaptchaToken);
    }
}

function* confirmChanges(newPermission, oldPermission) {
    if (isEqual(newPermission, oldPermission)) {
        return true;
    }

    yield put(showConfirmChangesDialog());
    const action = yield take([CONFIRM_CHANGES_ACCEPTED, CONFIRM_CHANGES_REJECTED]);
    return action.type === CONFIRM_CHANGES_ACCEPTED;
}

function* handleHidePage() {
    const permissionState = yield select(getPermissionState);
    const confirmed = yield call(confirmChanges, permissionState.currentState, permissionState.startState);
    if (confirmed) {
        yield put(hidePage());
    }
}

function* handleCloseAgencyDialog() {
    const permissionState = yield select(getPermissionState);
    const confirmed = yield call(confirmChanges, permissionState.currentState, permissionState.startState);
    if (confirmed) {
        yield put(closeAgencyDialog());
    }
}

function* handleManagePermissionsCalled(action) {
    const { user } = action;
    yield put(updateRoleSelectionForm(user.role.type));
    const partialState = yield select(makeGetEditUserState(user));
    yield put(managePermissions(user, partialState));

    const snapshot = yield select(getSaveMemberData);
    yield put(setInitialPermissionSnapshot(snapshot));

    if (yield select(getBaseSourcesLoading)) {
        yield fork(restoreSourceRestriction, user);
    }
}

// The edit state expands a "not in" source restriction against the full source list, so when the shelf
// opens before org_networks arrives, redo the base_source form and snapshot row once it does.
// Save stays disabled until then.
export function* restoreSourceRestriction(user) {
    yield take(UPDATE_DATA_SOURCE_DATA);
    const { editMemberId, dimensionFilters } = yield select(makeGetEditUserState(user));
    const baseSource = dimensionFilters?.base_source;
    if (!baseSource || (yield select(getEditMemberId)) !== editMemberId) {
        return;
    }
    yield put(setDimensionFilter('base_source', baseSource.values));
    yield put(addFutureChanged('base_source', true, baseSource.addFuture));

    const { startState } = yield select(getPermissionState);
    const { role } = yield select(getSaveMemberData);
    const freshRow = role.row.find(r => r.dimension === 'base_source');
    if (freshRow) {
        yield put(
            setInitialPermissionSnapshot({
                ...startState,
                role: {
                    ...startState.role,
                    row: startState.role.row.map(r => (r.dimension === 'base_source' ? freshRow : r)),
                },
            })
        );
    }
}

function* handleSnapshotPermissions() {
    const snapshot = yield select(getSaveMemberData);
    yield put(setInitialPermissionSnapshot(snapshot));
}

// ---------------------------------/
//      WATCHERS
// ---------------------------------/

function* onload() {
    yield call(updatePageData);
}

function* watchDeleteUserClicked() {
    yield takeEvery(DELETE_MEMBER_CLICKED, handleDeleteMemberClicked);
}

function* watchResendPermissions() {
    yield takeEvery(RESEND_INVITATION, handleResendInvitation);
}

function* watchManagePermissionsFinished() {
    yield takeEvery(MANAGE_PERMISSIONS_FINISHED, handleManagePermissionsFinished);
}

function* watchHidePage() {
    yield takeEvery(HIDE_PAGE_CALLED, handleHidePage);
}

function* watchCloseAgencyDialog() {
    yield takeEvery(CLOSE_AGENCY_DIALOG_CALLED, handleCloseAgencyDialog);
}

function* watchManagePermissionsCalled() {
    yield takeEvery(MANAGE_PERMISSIONS_CALLED, handleManagePermissionsCalled);
}

function* watchCreateNewMember() {
    yield takeEvery(CREATE_NEW_MEMBER, handleSnapshotPermissions);
}

function* watchAgencySelected() {
    yield takeEvery(UPDATE_AGENCY_SELECTION_FORM, handleSnapshotPermissions);
}

// ---------------------------------/
//      PAGINATED SECTION SAGAS
// ---------------------------------/

const _selectSection = section => state => state.teamManagement[section];

let _nextRequestId = 1;
const _newRequestId = () => _nextRequestId++;

export function* fetchPaginatedSection(section, apiFn, { mode = 'replace', offset, extra } = {}) {
    const slice = yield select(_selectSection(section));
    const requestId = _newRequestId();
    const resolvedOffset = offset !== undefined ? offset : mode === 'append' ? slice.items.length : 0;
    const params = {
        offset: resolvedOffset,
        limit: slice.limit,
        // The slice stores the raw value the user typed (for the input). Lowercase only
        // here, at the BE-request boundary.
        search: (slice.search || '').toLowerCase(),
        sortBy: slice.sortBy,
        sortDir: slice.sortDir,
        extra,
    };
    yield put(fetchSectionRequested(section, params, mode, requestId));
    try {
        const response = yield call(apiFn, params);
        yield put(fetchSectionSucceeded(section, response, mode, requestId, params.search));
    } catch (error) {
        yield put(fetchSectionFailed(section, error, requestId));
    }
}

export const _apiBySection = {
    activeUsers: { apiFn: 'getOrgUsersPaginated', extra: { status: 'active' } },
    inactiveUsers: { apiFn: 'getOrgUsersPaginated', extra: { status: 'inactive' } },
    invites: { apiFn: 'getOrgInvitesPaginated', extra: undefined },
    agencies: { apiFn: 'getAgenciesPaginated', extra: undefined },
};

function* _loadSection(section, action) {
    const { apiFn, extra } = _apiBySection[section];
    yield call(fetchPaginatedSection, section, sagas[apiFn].bind(sagas), {
        mode: action.mode || 'replace',
        offset: action.offset,
        extra,
    });
}

export const refreshSection = (section, mode = 'replace', offset) => ({
    type: `REFRESH_SECTION_${section}`,
    mode,
    offset,
});

export function* watchSectionRefresh() {
    yield all(
        SECTION_KEYS.map(section =>
            fork(function*() {
                yield takeLatest(`REFRESH_SECTION_${section}`, function*(action) {
                    yield call(_loadSection, section, action);
                });
            })
        )
    );
}

// ---------------------------------/
//      SECTION TRIGGER SAGAS
// ---------------------------------/

export function* handleSearchChanged({ section, search }) {
    yield put(setSectionSearch(section, search));
    yield put(resetSectionPagination(section));
    yield put(refreshSection(section));
}

export function* handleSortChanged({ section, sortBy, sortDir }) {
    yield put(setSectionSort(section, sortBy, sortDir));
    yield put(resetSectionPagination(section));
    yield put(refreshSection(section));
}

export function* refreshSections(sections) {
    for (const s of sections) {
        yield put(resetSectionPagination(s));
        yield put(refreshSection(s));
    }
}

// Refresh after a single row was removed. If the current page would be empty after the
// deletion (current offset is past the new total), step one page back so the user lands
// on the previous page instead of an empty current page.
export function* refreshSectionAfterDelete(section) {
    const slice = yield select(_selectSection(section));
    const newTotal = Math.max(0, (slice.total || 0) - 1);
    const limit = slice.limit || 1;
    let targetOffset = slice.offset || 0;
    if (targetOffset >= newTotal && targetOffset > 0) {
        // Snap to the last non-empty page. Stepping back exactly one page would still land
        // on an empty page if local total is stale (e.g. another tab deleted multiple rows).
        targetOffset = Math.max(0, Math.floor((newTotal - 1) / limit) * limit);
    }
    yield put(resetSectionPagination(section));
    yield put(refreshSection(section, 'replace', targetOffset));
}

export function* handleDeactivateUser() {
    // The deactivated user leaves activeUsers (use page-back logic) and appears in
    // inactiveUsers (a fresh load is fine — appearing rows can't strand a page).
    yield call(refreshSectionAfterDelete, 'activeUsers');
    yield call(refreshSections, ['inactiveUsers']);
}

export function* handleDeleteInviteSuccess() {
    yield call(refreshSectionAfterDelete, 'invites');
}

export function* handleEditUserSuccess() {
    yield call(refreshSections, ['activeUsers', 'inactiveUsers']);
}

// Refresh the unfiltered tab-badge count for a section. The post-save auto-search filters
// the section's main fetch (and its requestId supersedes the create-refresh), so overallTotal
// would never update from the page fetch. This issues a minimal unfiltered request whose only
// purpose is to read the true total and set overallTotal directly (not requestId-gated).
export function* refreshSectionOverallTotal(section) {
    const slice = yield select(_selectSection(section));
    const { apiFn, extra } = _apiBySection[section];
    try {
        const response = yield call(sagas[apiFn].bind(sagas), {
            offset: 0,
            limit: 1,
            search: '',
            sortBy: slice.sortBy,
            sortDir: slice.sortDir,
            extra,
        });
        yield put(setSectionOverallTotal(section, response.total));
    } catch (error) {
        // Best-effort badge refresh; ignore failures.
    }
}

export function* handleCreateInviteSuccess() {
    yield call(refreshSections, ['invites']);
    yield call(refreshSectionOverallTotal, 'invites');
}

export function* handleCreateAgencySuccess() {
    yield call(refreshSections, ['agencies']);
    yield call(refreshSectionOverallTotal, 'agencies');
}

const SEARCH_DEBOUNCE_MS = 300;

export function* watchSectionTriggers() {
    // Debounce per section so a single keystroke that fans out to every visible section
    // produces one refresh per section. A single shared debounce would cancel all but the
    // last section's action, leaving the others without a refresh.
    for (const sectionKey of SECTION_KEYS) {
        yield debounce(
            SEARCH_DEBOUNCE_MS,
            action => action.type === SECTION_SEARCH_INPUT_CHANGED && action.section === sectionKey,
            handleSearchChanged
        );
    }
    yield takeEvery(SECTION_SORT_CLICKED, handleSortChanged);
    yield takeEvery(DELETE_USER_DISABLED, handleDeactivateUser);
    yield takeEvery(DELETE_USER_INVITE_REVOKED, handleDeleteInviteSuccess);
    yield takeEvery(USER_PERMISSIONS_UPDATED, handleEditUserSuccess);
    yield takeEvery(INVITE_CREATED, handleCreateInviteSuccess);
    yield takeEvery(AGENCY_SAVED, handleCreateAgencySuccess);
}

const watchers = [
    watchDeleteUserClicked,
    watchResendPermissions,
    watchManagePermissionsFinished,
    watchHidePage,
    watchManagePermissionsCalled,
    watchCloseAgencyDialog,
    watchCreateNewMember,
    watchAgencySelected,
    watchSectionRefresh,
    watchSectionTriggers,
];

export { onload, watchers };
